import { nativeQueueManager } from '@/lib/nativeSync/nativeQueueManager'
import type { QueueItemT } from '@/lib/types/bgSyncTypes'
import { markupSaveOf } from '@/lib/annotation/queuedMarkupSave'
import usePendingMarkupStore, { type QueuedMarkupRow } from '@/store/usePendingMarkupStore'

/**
 * Keeps usePendingMarkupStore in step with the offline queue: every queue change re-reads the
 * queue's markup saves, and a save whose row is gone after replay keeps its overlay for
 * REPLAY_SETTLE_MS — the parent issue/comment broadcast is published after the PATCH response
 * (kernel.terminate), and a read started before the replay still answers with the old markup.
 */
export const REPLAY_SETTLE_MS = 15_000

export function queuedMarkupRows(entries: QueueItemT[]): QueuedMarkupRow[] {
  const rows: QueuedMarkupRow[] = []
  for (const entry of entries) {
    const save = markupSaveOf(
      entry.requestData?.method ?? entry.metadata?.method,
      entry.requestData?.url,
      entry.requestData?.body,
    )
    if (!save) continue
    // Exactly the rows performSync replays: a licence hold or a pending retry is not a
    // permanent failure, so those saves stay on screen.
    rows.push({ rowId: entry.id, save, active: !entry.metadata?.permanentFailure })
  }
  return rows
}

let readSeq = 0
let started: { ready: Promise<void>; stop: () => void } | null = null
const settleTimers = new Map<string, ReturnType<typeof setTimeout>>()

/** Re-read the queue now; a read overtaken by a later one is dropped. */
export async function refreshPendingMarkup(): Promise<void> {
  const seq = ++readSeq
  const entries = await nativeQueueManager.getAllEntries()
  if (seq !== readSeq) return
  const replayed = usePendingMarkupStore.getState().applyQueue(queuedMarkupRows(entries))
  for (const uuid of replayed) scheduleSettle(uuid)
}

function scheduleSettle(uuid: string) {
  clearTimeout(settleTimers.get(uuid))
  settleTimers.set(
    uuid,
    setTimeout(() => {
      settleTimers.delete(uuid)
      if (usePendingMarkupStore.getState().saves[uuid]?.phase === 'replayed') {
        usePendingMarkupStore.getState().release(uuid)
      }
    }, REPLAY_SETTLE_MS),
  )
}

/**
 * Start following the queue, once per app. `ready` settles after the first snapshot (or its
 * failure), so the cold start can wait for markup queued before a reload.
 */
export function startPendingMarkupSync(): { ready: Promise<void>; stop: () => void } {
  if (started) return started
  const unsubscribe = nativeQueueManager.addListener(() => {
    void refreshPendingMarkup().catch(() => undefined)
  })
  const ready = refreshPendingMarkup().catch(error => {
    console.error('[PendingMarkup] Initial queue read failed', error)
  })
  started = {
    ready,
    stop: () => {
      unsubscribe()
      for (const timer of settleTimers.values()) clearTimeout(timer)
      settleTimers.clear()
      readSeq++
      started = null
    },
  }
  return started
}
